import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { describeError } from "../api/errors";

type Toast = { id: number; text: string; tone: "ok" | "danger" | "info"; action?: { label: string; run: () => void }; sticky?: boolean };
type Api = { show: (t: Omit<Toast, "id">) => void; error: (e: unknown, prefix?: string) => void; ok: (text: string) => void };
const Ctx = createContext<Api | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const next = useRef(1);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((t) => t.id !== id)), []);
  const show = useCallback((t: Omit<Toast, "id">) => {
    const id = next.current++;
    setItems((xs) => [...xs.slice(-2), { ...t, id }]);
    if (!t.sticky) setTimeout(() => dismiss(id), t.tone === "danger" ? 7000 : 3500);
  }, [dismiss]);
  const api = useMemo<Api>(() => ({
    show,
    error: (e, prefix) => show({ tone: "danger", text: prefix ? `${prefix}: ${describeError(e)}` : describeError(e) }),
    ok: (text) => show({ tone: "ok", text }),
  }), [show]);
  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast tone-${t.tone}`}>
            <span>{t.text}</span>
            {t.action && <button type="button" className="toast-action" onClick={() => { t.action?.run(); dismiss(t.id); }}>{t.action.label}</button>}
            <button type="button" className="toast-close" aria-label="Dismiss" onClick={() => dismiss(t.id)}>×</button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): Api {
  const c = useContext(Ctx);
  if (!c) throw new Error("ToastProvider missing");
  return c;
}
